import { cn } from '@/lib/utils';
import { DataTest } from '@/types/data-test';
import * as React from 'react';

type CardWithFullHeaderProps = React.ComponentProps<'div'> & { fullHeader?: boolean; };

const Card = ({ className, fullHeader, ...props }: CardWithFullHeaderProps): React.JSX.Element => {
  return (
    <div className={cn(
      'bg-card text-card-foreground flex flex-col gap-ms rounded-md border shadow-md',
      !fullHeader && 'p-ms',
      className
    )}
    data-slot="card"
    {...props}
    />
  );
};

const CardHeader = ({ className, ...props }: React.ComponentProps<'div'>): React.JSX.Element => {
  return (
    <div className={cn(
      '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-2',
      className
    )}
    data-slot="card-header"
    {...props}
    />
  );
};

const CardTitle = ({ className, dataTest, ...props }: React.ComponentProps<'div'> & DataTest): React.JSX.Element => {
  return (
    <h3 className={cn('leading-none font-bold', className)}
      data-slot="card-title"
      data-test={dataTest}
      {...props}
    />
  );
};

const CardDescription = ({ className, dataTest, ...props }: React.ComponentProps<'div'> & DataTest): React.JSX.Element => {
  return (
    <div className={cn('text-muted-foreground text-sm', className)}
      data-slot="card-description"
      data-test={dataTest}
      {...props}
    />
  );
};

const CardAction = ({ className, ...props }: React.ComponentProps<'div'>): React.JSX.Element => {
  return (
    <div className={cn(
      'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
      className
    )}
    data-slot="card-action"
    {...props}
    />
  );
};

const CardContent = ({ className, fullHeader, ...props }: CardWithFullHeaderProps): React.JSX.Element => {
  return (
    <div className={cn(className, fullHeader && 'px-ms pb-ms')}
      data-slot="card-content"
      {...props}
    />
  );
};

const CardFooter = ({ className, ...props }: React.ComponentProps<'div'>): React.JSX.Element => {
  return (
    <div className={cn('flex items-center [.border-t]:pt-6', className)}
      data-slot="card-footer"
      {...props}
    />
  );
};

export {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
};
